import React, { useState } from "react";
import { Copy, Check, X } from "lucide-react";
import { TrashIcon } from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Button } from "components/Graphs/Button";
import { Badge } from "components/Graphs/Badge";
import { Input } from "components/Graphs/Input";
import { cx } from "components/Graphs/utils";
import Spinner from "components/UI-lib/Spinner";
import { ENABLE_SSH_COMMAND } from "components/BrevConnect/DeviceConnected/utils";

export type PortType = "TCP" | "UDP" | "HTTP" | "SSH";

export function getVisibleOverflowItems<T>(items: T[], maxItems: number): T[] {
  const hiddenCount = items.length - maxItems;
  return items.slice(0, hiddenCount === 1 ? maxItems + 1 : maxItems);
}

export function NeutralInlinePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md px-2 py-1 text-xs bg-zinc-800 text-white">
      {children}
    </span>
  );
}

export function NetworkTableLoadingRow({
  colSpan,
  label,
}: {
  colSpan: number;
  label: string;
}) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="px-3 py-6 text-center text-sm text-gray-500 dark:text-secondary"
      >
        <div className="flex items-center justify-center">
          <Spinner
            type="secondary"
            className="h-4 w-4 text-gray-500 dark:text-secondary"
          />
          <span>{label}</span>
        </div>
      </td>
    </tr>
  );
}

export function AllowedSourcesValue({
  allowedSources,
  maxVisible,
  onMoreClick,
}: {
  allowedSources: string[];
  maxVisible: number;
  onMoreClick?: () => void;
}) {
  if (
    allowedSources.length === 0 ||
    allowedSources.every((source) => source === "0.0.0.0/0")
  ) {
    return <NeutralInlinePill>Anywhere</NeutralInlinePill>;
  }

  const visibleSources = getVisibleOverflowItems(allowedSources, maxVisible);
  const hiddenCount = allowedSources.length - visibleSources.length;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visibleSources.map((source: string) => (
        <Badge key={source} variant="neutral">
          {source}
        </Badge>
      ))}
      {hiddenCount > 0 && onMoreClick && (
        <button
          type="button"
          onClick={onMoreClick}
          aria-label={`Show ${hiddenCount} more IP restrictions`}
          className="inline border-0 bg-transparent p-0 align-baseline text-xs font-medium text-highlight underline-offset-2 hover:text-highlightDarker hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight"
        >
          + {hiddenCount} more
        </button>
      )}
      {hiddenCount > 0 && !onMoreClick && (
        <span className="inline text-xs font-medium text-highlight">
          + {hiddenCount} more
        </span>
      )}
    </div>
  );
}

export function AllowedSourcesEditor({
  allowedSources,
  sourceInput,
  onSourceInputChange,
  onAddSource,
  onRemoveSource,
  helperText = "Use 0.0.0.0/0 to allow all. Remove all entries to clear the list.",
  titleClassName = "text-base font-semibold text-neutral-200",
}: {
  allowedSources: string[];
  sourceInput: string;
  onSourceInputChange: (value: string) => void;
  onAddSource: () => void;
  onRemoveSource: (source: string) => void;
  helperText?: string;
  titleClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <label className={titleClassName}>Allowed Sources</label>
      <div className="flex flex-row gap-2">
        <Input
          placeholder="0.0.0.0/0"
          value={sourceInput}
          onChange={(e) => onSourceInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddSource();
            }
          }}
          className="flex-1"
        />
        <Button
          variant="secondary"
          onClick={onAddSource}
          disabled={!sourceInput.trim()}
        >
          Add
        </Button>
      </div>
      <p className="text-xs text-neutral-500">{helperText}</p>
      <div className="flex flex-wrap gap-2">
        {allowedSources.map((source) => (
          <div
            key={source}
            className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-900 dark:bg-zinc-800 dark:text-zinc-200"
          >
            <span className="min-w-0 truncate">{source}</span>
            <button
              type="button"
              aria-label={`Remove allowed source ${source}`}
              onClick={() => onRemoveSource(source)}
              className="shrink-0 rounded p-0.5 text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight dark:text-zinc-400"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScrollableNetworkDialogContent({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogContent>) {
  return (
    <DialogContent
      className={cx(
        "grid max-h-[calc(100vh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden sm:max-w-[480px]",
        className,
      )}
      {...props}
    />
  );
}

export function ScrollableNetworkDialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cx(
        "flex min-h-0 flex-col gap-4 overflow-y-auto pr-2",
        className,
      )}
      {...props}
    />
  );
}

export function productNameToDisplayName(
  productName: string | undefined,
): string | undefined {
  if (!productName) return undefined;
  const normalizedProductName = productName.toUpperCase().replace(/[\s_]/g, "");
  if (normalizedProductName.includes("DGXSPARK")) {
    return "DGX Spark";
  }
  if (normalizedProductName.includes("DGXSTATION")) {
    return "DGX Station";
  }
  return undefined;
}

export const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="rounded p-0.5 text-neutral-600"
      title="Copy"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-400" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
};

export const EnableSSHBanner: React.FC = () => (
  <div className="mb-6 w-full rounded-md border border-neutral-700 bg-neutral-900 p-4">
    <p className="text-sm font-medium text-white mb-1">
      SSH access hasn&apos;t been enabled on this node yet.
    </p>
    <p className="text-sm text-neutral-400 mb-3">
      Run the following command directly on the node using the Brev CLI to
      enable SSH access:
    </p>
    <div className="flex items-center gap-2 bg-neutral-950 rounded px-3 py-2 font-mono text-sm text-neutral-200">
      <span className="flex-1">{ENABLE_SSH_COMMAND}</span>
      <CopyButton value={ENABLE_SSH_COMMAND} />
    </div>
  </div>
);

export const ConfirmDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmLoadingLabel?: string;
  details?: Array<{ label: string; value: string }>;
  onConfirm: () => Promise<void>;
}> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmLoadingLabel,
  details = [],
  onConfirm,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-2">
            <TrashIcon className="h-5 w-5 text-white" />
            <DialogTitle className="text-white">{title}</DialogTitle>
          </div>
          <DialogDescription className="text-neutral-400">
            {description}
          </DialogDescription>
        </DialogHeader>
        {details.length > 0 && (
          <div className="min-w-0 rounded-md border border-zinc-800 bg-secondaryBg/40 p-3 text-sm text-secondary">
            {details.map((detail) => (
              <div
                key={detail.label}
                className="flex min-w-0 items-center gap-4 py-1"
              >
                <span className="w-28 shrink-0 text-secondary">
                  {detail.label}
                </span>
                <span
                  title={detail.value}
                  className="block min-w-0 max-w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-right font-mono text-primary"
                >
                  {detail.value}
                </span>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            isLoading={isSubmitting}
            loadingText={confirmLoadingLabel ?? confirmLabel}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
